import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { filter } from 'lodash-es';
import { parse } from 'query-string';
import styled from 'styled-components';
import { Alert, Container, Header, Steps, Typography as T } from '@dev/ee-components';
import { ConsentTracking, ContactStatus } from '@dev/ee-api/ee-api-models';
import { safelyDecodeBase64Url, safelyEncodeBase64Url } from '@dev/ee-tools';
import { push as Push } from 'redux-first-history';
import { useSecurity } from '@/services/hooks/useSecurity';
import { GetMailchimpConnectionParams } from '@/store/integrations/actions';
import { getUploadStore, DeleteUploadedFile } from '@/store/upload/actions';
import { ClearUploadData, getContactsStore } from '@/store/contacts/actions';
import { GetContactFieldsStart, GetFields } from '@/store/customFields/actions';
import { ListsLoadStart } from '@/store/lists/actions';
import { LoadUserFiles } from '@/store/userfiles/actions';
import { ContactsPaths } from '@/services/dictionaries/Paths';
import { TermsConfirmation } from './Components/TermsConfirmation';
import { SourceType } from './Components/SourceType';
import { AddContacts } from './Components/AddContacts';
import { CustomFields } from './Components/CustomFields';
import { AssignContacts } from './Components/AssignContacts';
import { Analytics } from './Components/Analytics';
import { getPathParams } from '../helperFunctions';
import { UploadContactField } from './ContactsUpload';

export const ButtonFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    padding-top: 2.4rem;
    border-top: 1px solid #e2e8f0;
    margin-top: 1.5rem;
    & > div + div {
        margin-right: 1.2rem;
    }
    .MuiButton-textSecondary {
        margin-right: 1rem;
        padding-left: 1rem;
        padding-right: 1rem;
    }
`;

export const ALL_CONTACTS_LIST = '0'; //number sent to backend as listname when we are adding to All Contacts

export enum STEPS {
    TERMS_CONFIRMATION = 1,
    SOURCE_SELECTION = 2,
    CONTACT_ADDING_METHODS = 3,
    ADDING_AND_FIELDS_REVIEW = 4,
    CONTACT_ASSIGNING = 5,
}

export const encodingArray = [
    {
        value: 'utf-8',
        label: 'Unicode (UTF-8)',
    },
    {
        value: 'GB18030',
        label: 'Chinese Simplified',
    },
    {
        value: 'x-mac-chinesesimp',
        label: 'Chinese Simplified (Macintosh)',
    },
    {
        value: 'x-mac-chinesetrad',
        label: 'Chinese Traditional (Macintosh)',
    },
    {
        value: 'windows-1258',
        label: 'Vietnamese (Windows)',
    },
    {
        value: 'big5',
        label: 'Big5 (Taiwan, Hong Kong, Macau)',
    },
    {
        value: 'euc-kr',
        label: 'Korean (EUC)',
    },
    {
        value: 'x-mac-korean',
        label: 'Korean (Macintosh)',
    },
    {
        value: 'windows-874',
        label: 'Thai (Windows)',
    },
    {
        value: 'x-mac-thai',
        label: 'Thai (Macintosh)',
    },
    {
        value: 'windows-1253',
        label: 'Greek (Windows)',
    },
    {
        value: 'x-mac-greek',
        label: 'Greek (Macintosh)',
    },
    {
        value: 'windows-1254',
        label: 'Turkish (Windows)',
    },
    {
        value: 'x-mac-turkish',
        label: 'Turkish (Macintosh)',
    },
    {
        value: 'windows-1256',
        label: 'Arabic (Windows)',
    },
    {
        value: 'x-mac-arabic',
        label: 'Arabic (Macintosh)',
    },
    {
        value: 'windows-1255',
        label: 'Hebrew (Windows)',
    },
    {
        value: 'x-mac-hebrew',
        label: 'Hebrew (Macintosh)',
    },
    {
        value: 'windows-1250',
        label: 'Central European (Windows)',
    },
    {
        value: 'x-mac-ce',
        label: 'Central European (Macintosh)',
    },
    {
        value: 'Windows-1252',
        label: 'Western European (Windows)',
    },
    {
        value: 'macintosh',
        label: 'Western European (Macintosh)',
    },
    {
        value: 'windows-1251',
        label: 'Cyrillic (Windows)',
    },
    {
        value: 'x-mac-cyrillic',
        label: 'Cyrillic (Macintosh)',
    },
    {
        value: 'iso-8859-13',
        label: 'Estonian',
    },
    {
        value: 'x-mac-romanian',
        label: 'Romanian (Macintosh)',
    },
    {
        value: 'x-mac-ukrainian',
        label: 'Ukrainian (Macintosh)',
    },
    {
        value: 'x-mac-croatian',
        label: 'Croatian (Macintosh)',
    },
    {
        value: 'windows-1257',
        label: 'Baltic (Windows)',
    },
    {
        value: 'x-mac-icelandic',
        label: 'Icelandic (Macintosh)',
    },
    {
        value: 'x-mac-japanese',
        label: 'Japanese (Macintosh)',
    },
    {
        value: 'shift_jis',
        label: 'Japanese',
    },
    {
        value: 'x-iscii-de',
        label: 'ISCII Devanagari',
    },
    {
        value: 'x-iscii-be',
        label: 'ISCII Bengali',
    },
    {
        value: 'x-iscii-ta',
        label: 'ISCII Tamil',
    },
    {
        value: 'x-iscii-te',
        label: 'ISCII Telugu',
    },
    {
        value: 'x-iscii-as',
        label: 'ISCII Assamese',
    },
    {
        value: 'x-iscii-or',
        label: 'ISCII Oriya',
    },
    {
        value: 'x-iscii-ka',
        label: 'ISCII Kannada',
    },
    {
        value: 'x-iscii-ma',
        label: 'ISCII Malayalam',
    },
    {
        value: 'x-iscii-gu',
        label: 'ISCII Gujarati',
    },
    {
        value: 'x-iscii-pa',
        label: 'ISCII Punjabi',
    },
];

export const getDecodedListName = (listname): string => (listname ? safelyDecodeBase64Url(listname) : null);

export const ContactsUpload = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { segmentname, listname } = useParams<{ segmentname: string; listname: string }>();
    const { contactPattern } = useSelector(getContactsStore);
    const { isAnalyticsOpen } = useSelector(getUploadStore);
    const [path, pathParam, breadcrumbName] = getPathParams(segmentname, listname);
    const { isReseller } = useSecurity();
    const [currentStep, setCurrentStep] = useState(STEPS.TERMS_CONFIRMATION);
    const [pattern, setPattern] = useState({});
    const [consentTracking, setConsentTracking] = useState(ConsentTracking.Unknown);
    const [status, setStatus] = useState(ContactStatus.Active);
    const [listName, setListName] = useState(listname ? getDecodedListName(listname) : ALL_CONTACTS_LIST);
    const [errorFields, setErrorFields] = useState(false);
    const [overrideCustomFields, setOverrideCustomFields] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [sourceType, setSourceType] = useState('upload');
    const [encoding, setEncoding] = useState('utf-8');
    const [contactsPasted, setContactsPasted] = useState([]);
    const [recognizedEmails, setRecognizedEmails] = useState(null);
    const [contactFieldsArray, setContactFieldsArray] = useState<UploadContactField[]>([]);
    const [createList, setCreateList] = useState(false);
    const [isValidListName, setValidListName] = useState(true);

    const resetUploadFlow = (step = STEPS.SOURCE_SELECTION): void => {
        dispatch(DeleteUploadedFile());
        dispatch(ClearUploadData());
        setContactFieldsArray([]);
        dispatch(GetFields(0, 0));

        setCurrentStep(step);
        setPattern({});
        setConsentTracking(ConsentTracking.Unknown);
        setStatus(ContactStatus.Active);
        setListName(listname ? getDecodedListName(listname) : ALL_CONTACTS_LIST);
        setErrorFields(false);
        setOverrideCustomFields(false);
    };

    const clearUploadedFileData = (): void => {
        dispatch(DeleteUploadedFile());
        dispatch(ClearUploadData());
        dispatch(GetFields(0, 0));

        setContactFieldsArray([]);
        setConsentTracking(ConsentTracking.Unknown);
        setStatus(ContactStatus.Active);
        setListName(listname ? getDecodedListName(listname) : ALL_CONTACTS_LIST);
        setPattern({});
        setErrorFields(false);
        setOverrideCustomFields(false);
    };
    const getSteps = (): { title: string; content: JSX.Element }[] => [
        {
            title: t('confirmationofterms'),
            content: (
                <TermsConfirmation
                    termsAccepted={termsAccepted}
                    setTermsAccepted={setTermsAccepted}
                    setCurrentStep={setCurrentStep}
                />
            ),
        },
        {
            title: t('howyouwanttoaddcontacts'),
            content: (
                <SourceType
                    currentStep={currentStep}
                    termsAccepted={termsAccepted}
                    sourceType={sourceType}
                    setSourceType={setSourceType}
                    setCurrentStep={setCurrentStep}
                />
            ),
        },
        {
            title: t('addcontacts'),
            content: (
                <AddContacts
                    encoding={encoding}
                    setEncoding={setEncoding}
                    sourceType={sourceType}
                    contactsPasted={contactsPasted}
                    setContactsPasted={setContactsPasted}
                    setRecognizedEmails={setRecognizedEmails}
                    setCurrentStep={setCurrentStep}
                    clearUploadedFileData={clearUploadedFileData}
                    resetUploadFlow={resetUploadFlow}
                />
            ),
        },
        {
            title: t('contactfields'),
            content: (
                <CustomFields
                    recognizedEmails={recognizedEmails}
                    sourceType={sourceType}
                    setCurrentStep={setCurrentStep}
                    errorFields={errorFields}
                    setErrorFields={setErrorFields}
                    setPattern={setPattern}
                    setContactFieldsArray={setContactFieldsArray}
                    overrideCustomFields={overrideCustomFields}
                    setOverrideCustomFields={setOverrideCustomFields}
                />
            ),
        },
        {
            title: t('assigncontacts'),
            content: (
                <div>
                    <AssignContacts
                        sourceType={sourceType}
                        setCurrentStep={setCurrentStep}
                        contactsPasted={contactsPasted}
                        pattern={pattern}
                        encoding={encoding}
                        consentTracking={consentTracking}
                        setConsentTracking={setConsentTracking}
                        status={status}
                        setStatus={setStatus}
                        createList={createList}
                        setCreateList={setCreateList}
                        setListName={setListName}
                        listName={listName}
                        isValidListName={isValidListName}
                        setValidListName={setValidListName}
                        overrideCustomFields={overrideCustomFields}
                    />
                    {isAnalyticsOpen && <Analytics />}
                </div>
            ),
        },
    ];

    const steps = getSteps();

    const { pathname } = useLocation();
    useEffect(() => {
        const query = parse(location.search);
        const { code } = query;

        dispatch(LoadUserFiles());
        dispatch(GetContactFieldsStart());
        dispatch(ListsLoadStart({ limit: 0, offset: 0, modifyQueryString: false }));

        const openMailchimpImport = !isReseller && code && pathname === ContactsPaths.ContactsUploadImportMailchimp;

        resetUploadFlow(openMailchimpImport ? STEPS.CONTACT_ADDING_METHODS : STEPS.TERMS_CONFIRMATION);

        if (openMailchimpImport) {
            setTermsAccepted(true);
            setSourceType('mailchimp');
            dispatch(GetMailchimpConnectionParams(code as string));
        }

        return () => {
            dispatch(ClearUploadData());
            dispatch(GetFields(0, 0));
        };
    }, []);

    useEffect(() => {
        if (contactPattern) {
            dispatch(
                GetFields(
                    contactPattern.Example.length,
                    filter(contactFieldsArray, (entry): string => entry.systemName).length
                )
            );
        }
    }, [contactPattern, contactFieldsArray]);

    return (
        <Container variant="full">
            <Header
                header={{ title: t('uploadcontacts') }}
                breadcrumbs={{
                    text: listname || segmentname ? breadcrumbName : t('overview'),
                    onClick: () =>
                        dispatch(
                            Push(
                                listname || segmentname
                                    ? path.replace(pathParam, safelyEncodeBase64Url(breadcrumbName))
                                    : ContactsPaths.ContactsOverview
                            )
                        ),
                }}
            />
            <Steps steps={steps} activeStep={currentStep || STEPS.TERMS_CONFIRMATION} contentHorizontalMargin={56} />
        </Container>
    );
};

export const InvalidFileAlert = (alert: string, severity: 'success' | 'info' | 'warning' | 'error'): JSX.Element => {
    const { t } = useTranslation();

    return (
        <div>
            <Alert severity={severity} style={{ marginTop: '0' }}>
                <T variant="body1">{alert}</T>
            </Alert>
            <div>
                <T variant="body1">
                    {t('invalidfilemore')}{' '}
                    <a href="" target="_blank">
                        {t('here')}.
                    </a>
                </T>
            </div>
        </div>
    );
};

export default ContactsUpload;
